/* Autoplay and mouse-drag for the 2026 PDP's native scroll-snap carousels:
   the "Seen On You" UGC track and the Kimonix recommendation tracks.

   Why so little code
   ------------------
   Both tracks are already `overflow-x: auto` with `scroll-snap-type: x mandatory`,
   so touch flicks, trackpad two-finger swipes and horizontal wheel events are
   the browser's own and work today. Reimplementing them -- as the Splide-based
   PDP gallery has to, because it is not a scroller -- would mean fighting the
   platform for behaviour we already have.

   What is genuinely missing is only:
     - autoplay, one slide at a time
     - click-and-drag with a mouse, which a native scroller does not give you

   Opt in from markup:

     <ul data-carousel data-carousel-autoplay="3000"> ... </ul>

   `data-carousel-autoplay` is the interval in milliseconds; 0 or absent means
   no autoplay, drag only.

   Autoplay stops while anyone is plausibly interacting or cannot see it: on
   hover, on keyboard focus inside, during a drag, while the tab is hidden,
   while the track is out of view, and permanently under
   prefers-reduced-motion. Advancing a carousel someone is reading is the main
   way this feature goes wrong. */
(function () {
  if (window.EttikaCarousel) return;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var DRAG_THRESHOLD = 4; // px before a press counts as a drag rather than a click

  /* Where each slide actually sits, measured rather than assumed.

     Not item width + gap: both tracks set scroll-padding-inline (the UGC track
     is full-bleed on mobile, the Kimonix track has a gutter), and the snap
     position is the item's offset minus that padding. Deriving it from the real
     boxes keeps this correct across both carousels and both breakpoints. */
  function itemOffsets(track) {
    var origin = track.getBoundingClientRect().left - track.scrollLeft;
    var pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
    var out = [];
    for (var i = 0; i < track.children.length; i++) {
      out.push(Math.round(track.children[i].getBoundingClientRect().left - origin - pad));
    }
    return out;
  }

  function atEnd(track) {
    return track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
  }

  /* Both tracks set scroll-behavior: smooth in CSS, and `behavior: 'auto'` on
     scrollTo means "use the CSS value" -- not "instant". So an instant jump has
     to suspend the CSS for the one assignment. */
  function jumpTo(track, left) {
    var previous = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = left;
    track.style.scrollBehavior = previous;
  }

  function advance(track) {
    var offsets = itemOffsets(track);
    if (!offsets.length) return;

    /* Wrap rather than stop, so an unattended carousel keeps cycling. The wrap
       is instant: animating it would rewind the whole track past the shopper. */
    if (atEnd(track)) {
      jumpTo(track, 0);
      return;
    }
    for (var i = 0; i < offsets.length; i++) {
      if (offsets[i] > track.scrollLeft + 2) {
        track.scrollTo({ left: offsets[i], behavior: 'smooth' });
        return;
      }
    }
    jumpTo(track, 0);
  }

  function initAutoplay(track) {
    var interval = parseInt(track.getAttribute('data-carousel-autoplay'), 10);
    if (!interval || interval < 500 || REDUCED) return;

    var timer = null;
    var blockers = 0; // hover, focus, drag -- any one of them holds autoplay
    var visible = false;

    function running() {
      return timer !== null;
    }
    function play() {
      if (running() || blockers > 0 || !visible || document.hidden) return;
      timer = setInterval(function () {
        advance(track);
      }, interval);
    }
    function pause() {
      if (!running()) return;
      clearInterval(timer);
      timer = null;
    }
    function block() {
      blockers++;
      pause();
    }
    function unblock() {
      blockers = Math.max(0, blockers - 1);
      play();
    }

    track.addEventListener('mouseenter', block);
    track.addEventListener('mouseleave', unblock);
    track.addEventListener('focusin', block);
    track.addEventListener('focusout', unblock);
    track.addEventListener('ettika:dragstart', block);
    track.addEventListener('ettika:dragend', unblock);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) pause();
      else play();
    });

    /* Only run while the track is actually on screen. Without this, every
       carousel on the page would be scrolling itself in the background on a
       page that is already heavy. */
    if (typeof IntersectionObserver !== 'undefined') {
      new IntersectionObserver(
        function (entries) {
          visible = entries[0].isIntersecting;
          if (visible) play();
          else pause();
        },
        { threshold: 0.25 }
      ).observe(track);
    } else {
      visible = true;
      play();
    }
  }

  /* Mouse drag. Touch is left entirely to the browser: pointer-event dragging
     on a touch device would mean calling preventDefault on the scroller and
     hand-rolling momentum, which is strictly worse than what iOS already does. */
  function initDrag(track) {
    var down = false;
    var dragging = false;
    var startX = 0;
    var startScroll = 0;
    var snap = '';

    /* Images and links are natively draggable, and the browser starts that drag
       as soon as the pointer moves -- before our own threshold is reached. The
       result is a ghost image following the cursor and a track that never
       moves. Cancelling dragstart on the whole track is what actually makes
       click-and-drag work here; the CSS below only stops the selection
       highlight. */
    track.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      down = true;
      dragging = false;
      startX = event.clientX;
      startScroll = track.scrollLeft;
    });

    track.addEventListener('pointermove', function (event) {
      if (!down) return;
      var dx = event.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        dragging = true;
        track.setPointerCapture(event.pointerId);
        track.classList.add('is-dragging');
        /* Mandatory snapping fights a drag in progress -- the track jumps to
           the nearest slide on every move instead of following the pointer. Turn
           it off for the duration and restore it on release, which is what makes
           the release settle on a slide. */
        snap = track.style.scrollSnapType;
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
        track.dispatchEvent(new CustomEvent('ettika:dragstart'));
      }
      track.scrollLeft = startScroll - dx;
      event.preventDefault();
    });

    function end(event) {
      if (!down) return;
      down = false;
      if (!dragging) return;
      dragging = false;
      try {
        track.releasePointerCapture(event.pointerId);
      } catch (e) {
        /* capture may already be gone if the pointer left the window */
      }
      track.classList.remove('is-dragging');
      track.style.scrollSnapType = snap;
      track.style.scrollBehavior = '';
      track.dispatchEvent(new CustomEvent('ettika:dragend'));

      /* Settle on the nearest slide. Restoring scroll-snap alone does not
         re-snap an already-scrolled track in every browser, and rounding to a
         fixed step would ignore scroll-padding, so aim at a measured offset. */
      var offsets = itemOffsets(track);
      if (offsets.length) {
        var nearest = offsets[0];
        for (var i = 1; i < offsets.length; i++) {
          if (Math.abs(offsets[i] - track.scrollLeft) < Math.abs(nearest - track.scrollLeft)) {
            nearest = offsets[i];
          }
        }
        track.scrollTo({ left: nearest, behavior: 'smooth' });
      }
    }

    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);

    /* A drag that ends over a card must not also open that card. Capture phase,
       so this runs before the card's own click handlers. */
    track.addEventListener(
      'click',
      function (event) {
        if (!track.classList.contains('was-dragged')) return;
        track.classList.remove('was-dragged');
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
    track.addEventListener('ettika:dragend', function () {
      track.classList.add('was-dragged');
      setTimeout(function () {
        track.classList.remove('was-dragged');
      }, 0);
    });
  }

  function init(track) {
    if (track.dataset.carouselBound) return;
    track.dataset.carouselBound = '1';
    initDrag(track);
    initAutoplay(track);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var tracks = scope.querySelectorAll('[data-carousel]');
    for (var i = 0; i < tracks.length; i++) init(tracks[i]);

  }

  window.EttikaCarousel = { scan: scan };

  function start() {
    scan(document);
    /* The Kimonix tracks are filled after their fetch resolves, and the UGC
       track can be re-rendered by the theme editor, so pick up late arrivals. */
    new MutationObserver(function () {
      scan(document);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
